import React from "react";
import { Box, Typography } from "@mui/material";
import "../../assets/css/GoogleMapsPopUp.css";

type DealCompMapPopUpBase = {
  /** Renders to the right of the first row (e.g. info window close control). */
  firstRowTrailing?: React.ReactNode;
};

export type DealCompMapPopUpLoanProps = DealCompMapPopUpBase & {
  variant?: "loan";
  location: string | null;
  lender: string | null;
  borrower: string | null;
  loan_amount: number | null;
};

export type DealCompMapPopUpSalesProps = DealCompMapPopUpBase & {
  variant: "sales";
  buyer: string | null;
  vendor: string | null;
  currency: string | null;
  location: string | null;
  area_type: string | null;
  yield_pct: number | null;
  sales_price: number | null;
  price_per_area?: number | null;
  no_of_keys_or_rooms: number | null;
  price_per_key_room?: number | null;
  age_refurb_date?: string | null;
};

export type DealCompMapPopUpLeaseProps = DealCompMapPopUpBase & {
  variant: "lease";
  /** When set, the source value is a link; this URL is not shown as its own row. */
  web_url?: string | null;
  city: string | null;
  source: string | null;
  deal_name?: string | null;
  currency: string | null;
  asset_type: string | null;
  asset_name: string | null;
  lease_term: string | null;
  total_rent: number | null;
  rent_per_area?: number | null;
  tenant_name: string | null;
  age_refurb_date?: string | null;
};

export type DealCompMapPopUpProps =
  | DealCompMapPopUpLoanProps
  | DealCompMapPopUpSalesProps
  | DealCompMapPopUpLeaseProps;

const LOAN_FIELD_KEYS = [
  "location",
  "lender",
  "borrower",
  "loan_amount",
] as const;

const SALES_FIELD_KEYS = [
  "buyer",
  "vendor",
  "currency",
  "location",
  "area_type",
  "yield_pct",
  "sales_price",
  "price_per_area",
  "no_of_keys_or_rooms",
  "price_per_key_room",
  "age_refurb_date",
] as const;

const LEASE_FIELD_KEYS = [
  "city",
  "source",
  "deal_name",
  "currency",
  "asset_type",
  "asset_name",
  "lease_term",
  "total_rent",
  "rent_per_area",
  "tenant_name",
  "age_refurb_date",
] as const;

type LoanFieldKey = (typeof LOAN_FIELD_KEYS)[number];
type SalesFieldKey = (typeof SALES_FIELD_KEYS)[number];
type LeaseFieldKey = (typeof LEASE_FIELD_KEYS)[number];

function titleCaseLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Human-friendly labels for lease map card fields (overrides `titleCaseLabel`). */
const LEASE_FIELD_LABELS: Partial<Record<LeaseFieldKey, string>> = {
  deal_name: "Deal name",
  rent_per_area: "Rent per area",
  age_refurb_date: "Built / refurbished",
};

function leaseFieldLabel(key: LeaseFieldKey): string {
  const override = LEASE_FIELD_LABELS[key];
  if (override) return override;
  return titleCaseLabel(key);
}

const SALES_FIELD_LABELS: Partial<Record<SalesFieldKey, string>> = {
  price_per_area: "Price per area",
  price_per_key_room: "Price per key / room",
  age_refurb_date: "Built / refurbished",
};

function salesFieldLabel(key: SalesFieldKey): string {
  const override = SALES_FIELD_LABELS[key];
  if (override) return override;
  return titleCaseLabel(key);
}

function formatLoanFieldValue(value: string | number | null): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "-";
    return value.toLocaleString("en-GB");
  }
  const s = String(value).trim();
  return s.length ? s : "-";
}

function formatSalesFieldValue(
  key: SalesFieldKey,
  props: Pick<DealCompMapPopUpSalesProps, SalesFieldKey>,
): string {
  const raw = props[key];
  if (key === "yield_pct") {
    if (raw === null || raw === undefined) return "-";
    if (typeof raw === "number" && Number.isFinite(raw)) {
      return `${raw.toLocaleString("en-GB", { maximumFractionDigits: 4 })}%`;
    }
    return "-";
  }
  if (key === "sales_price") {
    const price = props.sales_price;
    if (price === null || price === undefined || !Number.isFinite(price)) {
      return "-";
    }
    const formatted = price.toLocaleString("en-GB");
    const cur = props.currency?.trim();
    return cur ? `${formatted} ${cur}` : formatted;
  }
  if (key === "price_per_area" || key === "price_per_key_room") {
    const v = props[key];
    if (v === null || v === undefined || !Number.isFinite(v)) {
      return "-";
    }
    const formatted = v.toLocaleString("en-GB");
    const cur = props.currency?.trim();
    return cur ? `${formatted} ${cur}` : formatted;
  }
  if (key === "no_of_keys_or_rooms") {
    if (raw === null || raw === undefined) return "-";
    if (typeof raw === "number" && Number.isFinite(raw)) {
      return raw.toLocaleString("en-GB", { maximumFractionDigits: 0 });
    }
    return "-";
  }
  if (raw === null || raw === undefined) return "-";
  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) return "-";
    return raw.toLocaleString("en-GB");
  }
  const s = String(raw).trim();
  return s.length ? s : "-";
}

function formatLeaseFieldValue(
  key: LeaseFieldKey,
  props: Pick<DealCompMapPopUpLeaseProps, LeaseFieldKey>,
): string {
  if (key === "total_rent" || key === "rent_per_area") {
    const rent = props[key];
    if (rent === null || rent === undefined || !Number.isFinite(rent)) {
      return "-";
    }
    const formatted = rent.toLocaleString("en-GB");
    const cur = props.currency?.trim();
    return cur ? `${formatted} ${cur}` : formatted;
  }
  const raw = props[key];
  if (raw === null || raw === undefined) return "-";
  const s = String(raw).trim();
  return s.length ? s : "-";
}

function leaseFieldValueContent(
  key: LeaseFieldKey,
  fullProps: DealCompMapPopUpLeaseProps,
  useLightTheme: boolean,
): React.ReactNode {
  const valueTypographySx = {
    color: useLightTheme ? "#3D4A59" : "rgba(255, 255, 255, 0.9)",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: 1.35,
  };

  if (key === "source") {
    const text = formatLeaseFieldValue("source", fullProps);
    const rawUrl = fullProps.web_url;
    const url =
      typeof rawUrl === "string" && rawUrl.trim().length > 0
        ? rawUrl.trim()
        : null;
    if (!url || text === "-") {
      return (
        <Typography
          component="span"
          className="map-info-values"
          sx={valueTypographySx}
        >
          {text}
        </Typography>
      );
    }
    return (
      <Typography
        component="span"
        className="map-info-values"
        sx={valueTypographySx}
      >
        <a href={url} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      </Typography>
    );
  }

  return (
    <Typography
      component="span"
      className="map-info-values"
      sx={valueTypographySx}
    >
      {formatLeaseFieldValue(key, fullProps)}
    </Typography>
  );
}

function loanFieldRow(
  key: LoanFieldKey,
  props: Pick<DealCompMapPopUpLoanProps, LoanFieldKey>,
  useLightTheme: boolean,
) {
  return (
    <Box
      key={key}
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "baseline",
        columnGap: 0.5,
        rowGap: 0,
      }}
    >
      <Typography
        component="span"
        className="map-info-title"
        sx={{
          color: useLightTheme ? "#182737" : "#FFF",
          fontSize: "14px",
          fontWeight: 600,
        }}
      >
        {titleCaseLabel(key)}:
      </Typography>
      <Typography
        component="span"
        className="map-info-values"
        sx={{
          color: useLightTheme ? "#3D4A59" : "rgba(255, 255, 255, 0.9)",
          fontSize: "14px",
          fontWeight: 400,
          lineHeight: 1.35,
        }}
      >
        {formatLoanFieldValue(props[key])}
      </Typography>
    </Box>
  );
}

function salesFieldRow(
  key: SalesFieldKey,
  props: Pick<DealCompMapPopUpSalesProps, SalesFieldKey>,
  useLightTheme: boolean,
) {
  return (
    <Box
      key={key}
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "baseline",
        columnGap: 0.5,
        rowGap: 0,
      }}
    >
      <Typography
        component="span"
        className="map-info-title"
        sx={{
          color: useLightTheme ? "#182737" : "#FFF",
          fontSize: "14px",
          fontWeight: 600,
        }}
      >
        {salesFieldLabel(key)}:
      </Typography>
      <Typography
        component="span"
        className="map-info-values"
        sx={{
          color: useLightTheme ? "#3D4A59" : "rgba(255, 255, 255, 0.9)",
          fontSize: "14px",
          fontWeight: 400,
          lineHeight: 1.35,
        }}
      >
        {formatSalesFieldValue(key, props)}
      </Typography>
    </Box>
  );
}

function leaseFieldRow(
  key: LeaseFieldKey,
  fullProps: DealCompMapPopUpLeaseProps,
  useLightTheme: boolean,
) {
  return (
    <Box
      key={key}
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "baseline",
        columnGap: 0.5,
        rowGap: 0,
      }}
    >
      <Typography
        component="span"
        className="map-info-title"
        sx={{
          color: useLightTheme ? "#182737" : "#FFF",
          fontSize: "14px",
          fontWeight: 600,
        }}
      >
        {leaseFieldLabel(key)}:
      </Typography>
      {leaseFieldValueContent(key, fullProps, useLightTheme)}
    </Box>
  );
}

export const DealCompMapPopUp: React.FC<DealCompMapPopUpProps> = (props) => {
  const useLightTheme = true;
  const { firstRowTrailing, ...rest } = props;

  if (props.variant === "sales") {
    const fieldProps = rest as Pick<
      DealCompMapPopUpSalesProps,
      SalesFieldKey
    >;
    const [firstKey, ...restKeys] = SALES_FIELD_KEYS;

    return (
      <Box
        className="map-info-top-box"
        sx={{
          color: useLightTheme ? "#182737" : "#FFF",
          backgroundColor: useLightTheme ? "#FFF" : "#182737",
          borderRadius: "8px",
          minWidth: "200px",
        }}
      >
        <Box className="map-info-values-box-wrapper">
          {firstRowTrailing ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                justifyContent: "space-between",
                columnGap: 1,
                rowGap: 0,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  columnGap: 0.5,
                  rowGap: 0,
                }}
              >
                <Typography
                  component="span"
                  className="map-info-title"
                  sx={{
                    color: useLightTheme ? "#182737" : "#FFF",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  {salesFieldLabel(firstKey)}:
                </Typography>
                <Typography
                  component="span"
                  className="map-info-values"
                  sx={{
                    color: useLightTheme ? "#3D4A59" : "rgba(255, 255, 255, 0.9)",
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: 1.35,
                  }}
                >
                  {formatSalesFieldValue(firstKey, fieldProps)}
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0, lineHeight: 0 }}>
                {firstRowTrailing}
              </Box>
            </Box>
          ) : (
            salesFieldRow(firstKey, fieldProps, useLightTheme)
          )}
          {restKeys.map((key) =>
            salesFieldRow(key, fieldProps, useLightTheme),
          )}
        </Box>
      </Box>
    );
  }

  if (props.variant === "lease") {
    const fullLeaseProps = rest as DealCompMapPopUpLeaseProps;
    const [firstKey, ...restKeys] = LEASE_FIELD_KEYS;

    return (
      <Box
        className="map-info-top-box"
        sx={{
          color: useLightTheme ? "#182737" : "#FFF",
          backgroundColor: useLightTheme ? "#FFF" : "#182737",
          borderRadius: "8px",
          minWidth: "200px",
        }}
      >
        <Box className="map-info-values-box-wrapper">
          {firstRowTrailing ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                justifyContent: "space-between",
                columnGap: 1,
                rowGap: 0,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  columnGap: 0.5,
                  rowGap: 0,
                }}
              >
                <Typography
                  component="span"
                  className="map-info-title"
                  sx={{
                    color: useLightTheme ? "#182737" : "#FFF",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  {leaseFieldLabel(firstKey)}:
                </Typography>
                {leaseFieldValueContent(
                  firstKey,
                  fullLeaseProps,
                  useLightTheme,
                )}
              </Box>
              <Box sx={{ flexShrink: 0, lineHeight: 0 }}>
                {firstRowTrailing}
              </Box>
            </Box>
          ) : (
            leaseFieldRow(firstKey, fullLeaseProps, useLightTheme)
          )}
          {restKeys.map((key) =>
            leaseFieldRow(key, fullLeaseProps, useLightTheme),
          )}
        </Box>
      </Box>
    );
  }

  const fieldProps = rest as Pick<DealCompMapPopUpLoanProps, LoanFieldKey>;
  const [firstKey, ...restKeys] = LOAN_FIELD_KEYS;

  return (
    <Box
      className="map-info-top-box"
      sx={{
        color: useLightTheme ? "#182737" : "#FFF",
        backgroundColor: useLightTheme ? "#FFF" : "#182737",
        borderRadius: "8px",
        minWidth: "200px",
      }}
    >
      <Box className="map-info-values-box-wrapper">
        {firstRowTrailing ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "space-between",
              columnGap: 1,
              rowGap: 0,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "baseline",
                columnGap: 0.5,
                rowGap: 0,
              }}
            >
              <Typography
                component="span"
                className="map-info-title"
                sx={{
                  color: useLightTheme ? "#182737" : "#FFF",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                {titleCaseLabel(firstKey)}:
              </Typography>
              <Typography
                component="span"
                className="map-info-values"
                sx={{
                  color: useLightTheme ? "#3D4A59" : "rgba(255, 255, 255, 0.9)",
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: 1.35,
                }}
              >
                {formatLoanFieldValue(fieldProps[firstKey])}
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, lineHeight: 0 }}>{firstRowTrailing}</Box>
          </Box>
        ) : (
          loanFieldRow(firstKey, fieldProps, useLightTheme)
        )}
        {restKeys.map((key) => loanFieldRow(key, fieldProps, useLightTheme))}
      </Box>
    </Box>
  );
};